import { useCallback } from "react";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import {
  issueSnapshotFailure,
  issueSnapshotUploadStart,
} from "@adsk/acc-issues-core/state/redux/ducks/issueSnapshots";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { useSdk } from "@adsk/acc-issues-core/state/sdk/useSdk";
import { getDocsApi } from "@adsk/acc-issues-core/docs-service/DocsApi";
import { useCurrentProjectId } from "@adsk/alloy-provider";
import { Issue } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useSnapshotAbortController } from "./useSnapshotAbortController";

export const useUploadSnapshot = () => {
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();
  const { T } = useTranslations();
  const { getSignal, throwIfAborted, isAbortError } =
    useSnapshotAbortController();

  const docsSdk = getDocsApi();
  const sdk = useSdk();
  const projectId = useCurrentProjectId();

  return useCallback(
    async ({
      issue,
      snapshotBlob,
      hasMarkups,
      flowId,
    }: {
      issue: Pick<Issue, "id">;
      snapshotBlob: Blob | undefined;
      hasMarkups: boolean;
      flowId: string;
    }): Promise<string | undefined> => {
      if (!projectId || !snapshotBlob) {
        return;
      }

      try {
        // Avoid starting upload if the flow was aborted
        throwIfAborted(flowId);

        dispatch(
          issueSnapshotUploadStart({
            issueId: issue.id,
            snapshotBlob,
            hasMarkups,
            flowId,
          })
        );

        const { objectId: storageUrn } = await docsSdk.Projects.getStorage(
          projectId,
          { fileName: `issue-snapshot_${issue.id}_${Date.now()}.jpg` }
        );

        const { urls, uploadKey } = await sdk.Oss.getUploadLink(storageUrn);
        const [signedS3UploadUrl] = urls;

        await sdk.Oss.uploadObject(signedS3UploadUrl, snapshotBlob, {
          signal: getSignal(flowId),
        });

        // Avoid finalizing the upload if the flow was aborted
        throwIfAborted(flowId);

        // Let OSS know object is fully uploaded
        await sdk.Oss.finalizeUploadObject(storageUrn, uploadKey);

        return storageUrn;
      } catch (e) {
        if (isAbortError(e)) {
          return;
        }

        dispatch(issueSnapshotFailure({ issueId: issue.id }));
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate("thumbnailField.failureMessage"),
        });
      }
    },
    [
      T,
      dispatch,
      docsSdk.Projects,
      getSignal,
      throwIfAborted,
      isAbortError,
      projectId,
      sdk.Oss,
      showNotification,
    ]
  );
};
